'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

export interface UserStatus {
    /** Unused paid $7 logo credits. */
    logoCredits: number
    logosGeneratedToday: number
    isSubscriber: boolean
    isAdmin: boolean
    canGenerate: boolean
}

/**
 * Reads the signed-in user's credit state.
 *
 * Refreshes when the route changes and when the tab regains focus, rather than
 * polling the server on a timer forever.
 */
export function useUserStatus(enabled = true) {
    const router = useRouter()
    const [status, setStatus] = useState<UserStatus | null>(null)
    const [loading, setLoading] = useState(enabled)

    const refresh = useCallback(async () => {
        if (!enabled) return
        try {
            const res = await fetch('/api/user-status', { cache: 'no-store' })
            if (res.ok) {
                const data = await res.json()
                setStatus({
                    logoCredits: data.logoCredits ?? 0,
                    logosGeneratedToday: data.logosGeneratedToday ?? 0,
                    isSubscriber: !!data.isSubscriber,
                    isAdmin: !!data.isAdmin,
                    canGenerate: !!data.canGenerate
                })
            } else {
                setStatus(null)
            }
        } catch {
            // Network hiccup. Keep whatever we last knew.
        } finally {
            setLoading(false)
        }
    }, [enabled])

    useEffect(() => {
        if (!enabled) {
            setStatus(null)
            setLoading(false)
            return
        }
        refresh()
    }, [enabled, refresh, router.asPath])

    useEffect(() => {
        if (!enabled) return

        const onFocus = () => {
            if (document.visibilityState === 'visible') refresh()
        }

        window.addEventListener('focus', onFocus)
        document.addEventListener('visibilitychange', onFocus)
        return () => {
            window.removeEventListener('focus', onFocus)
            document.removeEventListener('visibilitychange', onFocus)
        }
    }, [enabled, refresh])

    return { status, loading, refresh }
}
